import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The expanded form on a match card: record / edit a frame result, plus a walkover shortcut
 * for scheduled matches. Fully self-contained — owns its own form state.
 */
export default function RecordResultForm({
  match, p1Name, p2Name, isCompleted, isScheduled, onRefresh, onClose,
}) {
  const [p1Frames, setP1Frames] = useState("");
  const [p2Frames, setP2Frames] = useState("");
  const [p1Break, setP1Break] = useState("");
  const [p2Break, setP2Break] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Pre-populate when editing a completed match
  useEffect(() => {
    if (isCompleted) {
      setP1Frames(String(match.player1_score || 0));
      setP2Frames(String(match.player2_score || 0));
      setP1Break(match.player1_highest_break ? String(match.player1_highest_break) : "");
      setP2Break(match.player2_highest_break ? String(match.player2_highest_break) : "");
    }
  }, [isCompleted, match.player1_score, match.player2_score, match.player1_highest_break, match.player2_highest_break]);

  const handleRecordResult = async () => {
    const f1 = parseInt(p1Frames) || 0;
    const f2 = parseInt(p2Frames) || 0;
    if (f1 === 0 && f2 === 0) { toast.error("Enter frame scores"); return; }
    if (f1 === f2) { toast.error("Match cannot end in a draw"); return; }
    setSubmitting(true);
    try {
      const payload = {
        player1_frames: f1,
        player2_frames: f2,
        player1_highest_break: parseInt(p1Break) || 0,
        player2_highest_break: parseInt(p2Break) || 0,
      };
      const res = await axios.post(`${API}/matches/${match.id}/record-result`, payload, { withCredentials: true });
      toast.success(res.data.message);
      onClose?.();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record result");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWalkover = async (winnerId) => {
    try {
      await axios.post(`${API}/matches/${match.id}/walkover`, { winner_id: winnerId }, { withCredentials: true });
      toast.success("Walkover recorded");
      onRefresh?.();
    } catch { toast.error("Failed to record walkover"); }
  };

  const handleNoResult = async () => {
    if (!window.confirm(
      "Mark this match as 'No Result'?\n\n"
      + "• Counts towards each player's matches-played tally\n"
      + "• Does NOT change frames/games won or lost\n"
      + "• Does NOT change points or ELO"
    )) return;
    try {
      await axios.post(`${API}/matches/${match.id}/no-result`, {}, { withCredentials: true });
      toast.success("Match marked as No Result");
      onClose?.();
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark as No Result");
    }
  };

  return (
    <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-3" data-testid={`match-result-form-${match.id}`}>
      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
        {isCompleted ? "Edit Result" : "Record Result"}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <PlayerScoreInputs
          playerName={p1Name}
          framesValue={p1Frames}
          breakValue={p1Break}
          onFramesChange={setP1Frames}
          onBreakChange={setP1Break}
          framesTestId={`p1-frames-${match.id}`}
          breakTestId={`p1-break-${match.id}`}
        />
        <PlayerScoreInputs
          playerName={p2Name}
          framesValue={p2Frames}
          breakValue={p2Break}
          onFramesChange={setP2Frames}
          onBreakChange={setP2Break}
          framesTestId={`p2-frames-${match.id}`}
          breakTestId={`p2-break-${match.id}`}
        />
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm" onClick={handleRecordResult} disabled={submitting}
          className="bg-primary text-primary-foreground"
          data-testid={`submit-result-${match.id}`}
        >
          <CheckCircle className="w-4 h-4 mr-1" weight="fill" />
          {submitting ? "Saving..." : isCompleted ? "Update Result" : "Save Result"}
        </Button>
        {isScheduled && (
          <div className="ml-auto flex items-center gap-1 flex-wrap justify-end">
            <span className="text-[10px] text-muted-foreground mr-1">Walkover:</span>
            <Button
              size="sm" variant="ghost" className="h-6 text-[10px] px-2"
              onClick={() => handleWalkover(match.player1_id)}
              data-testid={`walkover-p1-${match.id}`}
            >
              {(p1Name || "TBD").split(" ")[0]}
            </Button>
            <Button
              size="sm" variant="ghost" className="h-6 text-[10px] px-2"
              onClick={() => handleWalkover(match.player2_id)}
              data-testid={`walkover-p2-${match.id}`}
            >
              {(p2Name || "TBD").split(" ")[0]}
            </Button>
            <Button
              size="sm" variant="outline"
              className="h-6 text-[10px] px-2 ml-1 border-muted-foreground/40 text-muted-foreground hover:bg-muted/30"
              onClick={handleNoResult}
              data-testid={`no-result-${match.id}`}
              title="Counts towards matches-played but does not change points or ELO"
            >
              No Result
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerScoreInputs({
  playerName, framesValue, breakValue, onFramesChange, onBreakChange, framesTestId, breakTestId,
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium">{playerName}</Label>
      <div className="flex gap-2">
        <div className="flex-1">
          <Label className="text-[10px] text-muted-foreground">Frames Won</Label>
          <Input
            type="number" min="0" max="9" value={framesValue}
            onChange={(e) => onFramesChange(e.target.value)}
            className="h-9 bg-background border-border text-center font-bold"
            placeholder="0"
            data-testid={framesTestId}
          />
        </div>
        <div className="flex-1">
          <Label className="text-[10px] text-muted-foreground">High Break</Label>
          <Input
            type="number" min="0" value={breakValue}
            onChange={(e) => onBreakChange(e.target.value)}
            className="h-9 bg-background border-border text-center"
            placeholder="—"
            data-testid={breakTestId}
          />
        </div>
      </div>
    </div>
  );
}

